import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import { useState } from 'react';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { IAddress } from 'checkout/types/Address';
import { hideScrollbars } from 'styles/utils/hideScrollbars';
import { Loader } from 'shared/components/Loader';
import { useStoreSelector } from 'store/hooks/storeHooks';
import { t } from 'localization/utils/t';
import { CheckboxRadio } from 'shared/components/CheckboxRadio';

const TP = 'checkout.components.ShippingAddresses';

interface IShippingAddressProps {
  address: IAddress;
  defaultChecked?: boolean;
  onShippingAddressChange: (id: number) => Promise<void>;
  onShippingAddressRemoveClick: (id: number) => Promise<void>;
}

const ShippingAddress = ({
  address,
  defaultChecked = false,
  onShippingAddressChange,
  onShippingAddressRemoveClick,
}: IShippingAddressProps) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { user } = useStoreSelector((state) => state.auth);

  const handleAddressClick = async (id: number) => {
    await onShippingAddressChange(id);
  };

  const handleAddressDeleteClick = async (event, id: number) => {
    event.preventDefault();
    setIsLoading(true);
    await onShippingAddressRemoveClick(id);
    setIsLoading(false);
  };

  return (
    <ShippingAddress.Wrapper
      htmlFor={`${address.id}`}
      key={address.id}
      aria-label="shipping address"
    >
      <ShippingAddress.RadioOptions>
        <CheckboxRadio
          checked={defaultChecked}
          name="shipping-address"
          key={address.id}
          id={`${address.id}`}
          onChange={() => handleAddressClick(address.id)}
          data-qa="checkout_shipping_address_radio"
          type="checkbox"
        />
      </ShippingAddress.RadioOptions>
      <ShippingAddress.Label>
        <ShippingAddress.LabelText data-qa="checkout_shipping_address_name">
          {address.name}
        </ShippingAddress.LabelText>
        <ShippingAddress.LabelText data-qa="checkout_shipping_address_address1">
          {address.address1}
        </ShippingAddress.LabelText>
        <ShippingAddress.LabelText data-qa="checkout_shipping_address_address2">
          {address.address2}
        </ShippingAddress.LabelText>
        <ShippingAddress.LabelText>
          <span data-qa="checkout_shipping_address_city">{address.city}</span>,{' '}
          <span data-qa="checkout_shipping_address_stateCode">{address.stateCode}</span>{' '}
          <span data-qa="checkout_shipping_address_postalCode">{address.postalCode}</span>
        </ShippingAddress.LabelText>
        <ShippingAddress.LabelText data-qa="checkout_shipping_address_country">
          {address.country}
        </ShippingAddress.LabelText>
      </ShippingAddress.Label>
      {!user?.isGuest && (
        <ShippingAddress.ActionWrapper>
          <ShippingAddress.Action
            href=""
            onClick={(event) => handleAddressDeleteClick(event, address.id)}
            data-qa="checkout_shipping_address_delete"
          >
            {t(`${TP}.removeAddressButton`, 'Remove')}
          </ShippingAddress.Action>
        </ShippingAddress.ActionWrapper>
      )}
      {isLoading && (
        <ShippingAddress.LoaderWrapper data-qa="checkout_shipping_address_loader">
          <Loader />
        </ShippingAddress.LoaderWrapper>
      )}
    </ShippingAddress.Wrapper>
  );
};

export const ShippingAddresses = () => {
  const { addresses, deleteAddress, order, connectAddressToOrder } = useCheckoutContext();

  if (isEmpty(addresses)) {
    return null;
  }

  const shippingAddresses = Object.values(addresses)
    .filter((address) => address.addressType === 'shipping')
    // keep the current address on top
    .sort((a, b) => {
      if (a.id === order?.addressId) {
        return -1;
      }
      if (b.id === order?.addressId) {
        return 1;
      }
      return 0;
    });

  return (
    <ShippingAddresses.Fieldset>
      {shippingAddresses.map((address) => (
        <ShippingAddress
          key={address.id}
          defaultChecked={address.id === order?.addressId}
          address={address}
          onShippingAddressChange={connectAddressToOrder}
          onShippingAddressRemoveClick={deleteAddress}
        />
      ))}
    </ShippingAddresses.Fieldset>
  );
};

ShippingAddresses.Fieldset = styled.fieldset`
  border: none;
  padding: 0;
  max-height: 250px;
  overflow-y: scroll;
  position: relative;
  -webkit-overflow-scrolling: touch;
  ${hideScrollbars};
`;

ShippingAddress.Wrapper = styled.label`
  color: ${({ theme }) => theme.colors.black};
  display: grid;
  grid-template-columns: [checkbox] 28px [address] auto [action];
  padding-bottom: 20px;
  cursor: pointer;

  :last-child {
    padding-bottom: 0px;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.small}px) {
    font-size: 11.5px;
  }
`;

ShippingAddress.RadioOptions = styled.legend`
  display: flex;
  align-items: flex-start;
  margin-top: 2px;
  grid-column-start: checkbox;
  grid-column-end: address;
`;

ShippingAddress.Label = styled.span`
  grid-column-start: address;
  grid-column-end: action;
`;

ShippingAddress.LabelText = styled.p`
  margin-bottom: 4px;
`;

ShippingAddress.ActionWrapper = styled.div`
  grid-column-start: action;
  text-align: right;
  font-size: 11.5px;
`;

ShippingAddress.Action = styled.a`
  text-decoration: underline;
`;

ShippingAddress.LoaderWrapper = styled.div`
  grid-column-start: checkbox;
  grid-column-end: end;
`;
